import React from 'react';
import FeaturePageTemplate from '../components/FeaturePageTemplate';
import { motion } from 'framer-motion';
import styled from 'styled-components';

// ========== STYLED COMPONENTS ==========
const Section = styled.section`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
`;

const LineageVisualContainer = styled.div`
  position: relative;
  background: linear-gradient(135deg, #f0f4ff 0%, #eef1f5 100%);
  border-radius: 1rem;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin: 2rem 0;
  padding: 2rem;
`;

const LineageNode = styled(motion.div)`
  background: white;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  width: ${props => props.width || '180px'};
  z-index: 2;
  position: relative;
`;

const FeatureHighlight = styled(motion.div)`
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
  color: white;
  border-radius: 0.75rem;
  padding: 2rem;
  margin: 2rem 0;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
`;

const Card = styled.div`
  background: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  height: 100%;
`;

const Button = styled(motion.a)`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  border-radius: 0.5rem;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const PrimaryButton = styled(Button)`
  background: #8b5cf6;
  color: white;
  
  &:hover {
    background: #7c3aed;
  }
`;

// const SecondaryButton = styled(Button)`
//   background: transparent;
//   color: #8b5cf6;
//   border: 1px solid #8b5cf6;
  
//   &:hover {
//     background: rgba(139, 92, 246, 0.1);
//   }
// `;

// ========== ANIMATIONS ==========
const animations = {
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  },
  item: {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  }
};

// ========== MAIN COMPONENT ==========
const LineagePage = () => {
  // Main page icon
  const icon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );

  // Key benefits list
  const benefits = [
    "Column-level confidence with detailed visibility of data's journey",
    "Interactive, intuitive lineage visualization for faster understanding",
    "Automated impact analysis for planned changes with direct collaboration",
    "Deep BI tool integrations revealing complete relationships across your data ecosystem",
    "Seamless integration with the OpenLineage ecosystem and standards"
  ];

  // Feature details
  const details = [
    {
      title: "Column-Level Lineage",
      description: "Move beyond table-level lineage to gain column-level confidence in your data ecosystem.",
      items: [
        "Automated SQL parsing to extract column-level relationships",
        "Complete visibility from source columns to final dashboard elements",
        "Deep integration with BI tools showing native asset relationships",
        "Track transformations at the most granular level",
        "Visual distinction between column and table-level connections"
      ]
    },
    {
      title: "Interactive Lineage Exploration",
      description: "Experience a smooth, intuitive interface for exploring and understanding your data lineage.",
      items: [
        "Interactive controls to show as much or as little detail as needed",
        "Smooth navigation for seamless traversing across your data ecosystem",
        "Mini-map overview for navigating complex lineage graphs",
        "Personalized views with toggles for schema and annotations",
        "Zooming capabilities to focus on specific areas of interest"
      ]
    },
    {
      title: "Active Data Lineage",
      description: "Take action directly from your lineage view to collaborate and resolve issues faster.",
      items: [
        "Alert downstream users about changes to critical assets",
        "Create tickets for broken assets without switching context",
        "Message asset owners directly via integrated communication tools",
        "Download impact analysis reports with custom metadata",
        "Document decisions and changes within the lineage view"
      ]
    },
    {
      title: "Deep BI Integrations",
      description: "Qupid provides specialized integrations that understand the unique structures of each BI tool.",
      items: [
        "Tableau integrations showing worksheets, fields, and dashboards",
        "Looker integration revealing looks, explores, and derived tables",
        "Power BI connections mapping datasets, reports, and measures",
        "Custom BI tool integration framework for proprietary systems",
        "End-to-end visibility from warehouse to final visualization"
      ]
    },
    {
      title: "Automatic Metadata Propagation",
      description: "Let metadata flow through your data pipeline automatically for consistent governance.",
      items: [
        "PII classification propagation to protect sensitive data",
        "Critical asset tagging across upstream and downstream assets",
        "Automated column description inheritance for consistent documentation",
        "Policy propagation for seamless governance",
        "Change impact notification based on lineage relationships"
      ]
    }
  ];

  // Use cases
  const useCases = [
    {
      title: "Impact Analysis & Change Management",
      description: "Before making schema changes, see exactly which columns, reports, and dashboards will be affected, and notify stakeholders directly from the lineage view."
    },
    {
      title: "Data Privacy & Security",
      description: "Automatically propagate PII classifications from source columns to all derived assets, ensuring consistent protection of sensitive data across your entire ecosystem."
    },
    {
      title: "Root Cause Analysis",
      description: "When dashboards show incorrect data, trace back through column-level lineage to identify exactly where the issue originated and collaborate with owners to fix it."
    },
    {
      title: "Data Migration & Modernization",
      description: "Plan migrations with confidence by visualizing all dependencies at the column level, ensuring no critical connections are missed in the process."
    },
    {
      title: "Data Quality Management",
      description: "Understand how data quality issues propagate through your pipeline and implement validation at strategic points based on lineage insights."
    }
  ];

  // Integration platform list
  const integrationPlatforms = [
    'Snowflake', 'BigQuery', 'Redshift', 'dbt', 'Airflow', 
    'Tableau', 'Looker', 'PowerBI', 'Fivetran'
  ];

  return (
    <FeaturePageTemplate
      title="Advanced Column-Level Lineage"
      description="Gain complete visibility into your data's journey with Qupid's interactive column-level lineage, empowering you to understand relationships, take action, and ensure data governance across your entire ecosystem."
      icon={icon}
      benefits={benefits}
      details={details}
      useCases={useCases}
      additionalContent={
        <>
          {/* Interactive Lineage Visualization Section */}
          <Section>
            <SectionTitle>Interactive Data Lineage Visualization</SectionTitle>
            
            <LineageVisualContainer>
              <motion.div 
                className="grid grid-cols-3 gap-4 relative"
                variants={animations.container}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
              >
                {/* Source Systems Column */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg text-center mb-6">Source Systems</h4>
                  
                  {['CRM Database', 'ERP System', 'Web Analytics'].map((source, index) => (
                    <LineageNode 
                      key={`source-${index}`}
                      variants={animations.item}
                      whileHover={{ scale: 1.05 }}
                      className="mx-auto"
                    >
                      <div className="font-semibold mb-1">{source}</div>
                      <div className="text-sm text-gray-600">
                        {index === 0 ? 'customers' : index === 1 ? 'orders' : 'user_events'}
                      </div>
                      <div className="text-xs mt-2 text-purple-600">
                        {index === 0 ? '3' : index === 1 ? '5' : '4'} columns tracked →
                      </div>
                    </LineageNode>
                  ))}
                </div>
                
                {/* Transformation Layer Column */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg text-center mb-6">Transformations</h4>
                  
                  <LineageNode 
                    variants={animations.item}
                    whileHover={{ scale: 1.05 }}
                    className="mx-auto"
                  >
                    <div className="font-semibold mb-1">dbt Model</div>
                    <div className="text-sm text-gray-600">customer_360</div>
                    <div className="text-xs mt-2 text-purple-600">
                      <span className="bg-purple-100 px-1 rounded">JOIN</span> + <span className="bg-purple-100 px-1 rounded">CASE</span>
                    </div>
                  </LineageNode>
                  
                  <LineageNode 
                    variants={animations.item}
                    whileHover={{ scale: 1.05 }}
                    className="mx-auto bg-purple-50 border border-purple-200"
                  >
                    <div className="font-semibold mb-1">Stored Procedure</div>
                    <div className="text-sm text-gray-600">calculate_ltv</div>
                    <div className="text-xs mt-2 text-purple-600">
                      <span className="bg-purple-100 px-1 rounded">AGGREGATE</span>
                    </div>
                  </LineageNode>
                  
                  <LineageNode 
                    variants={animations.item}
                    whileHover={{ scale: 1.05 }}
                    className="mx-auto"
                  >
                    <div className="font-semibold mb-1">View</div>
                    <div className="text-sm text-gray-600">revenue_by_segment</div>
                    <div className="text-xs mt-2 text-purple-600">
                      <span className="bg-purple-100 px-1 rounded">GROUP BY</span>
                    </div>
                  </LineageNode>
                </div>
                
                {/* Consumption Layer Column */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg text-center mb-6">Consumption</h4>
                  
                  {[
                    {title: 'Tableau Dashboard', name: 'Customer Insights', metrics: '7 metrics tracked'},
                    {title: 'Looker Explore', name: 'Sales Analysis', metrics: '5 dimensions, 4 measures'},
                    {title: 'ML Model', name: 'Churn Prediction', metrics: '12 features used'}
                  ].map((item, index) => (
                    <LineageNode 
                      key={`consumption-${index}`}
                      variants={animations.item}
                      whileHover={{ scale: 1.05 }}
                      className="mx-auto"
                    >
                      <div className="font-semibold mb-1">{item.title}</div>
                      <div className="text-sm text-gray-600">{item.name}</div>
                      <div className="text-xs mt-2 text-indigo-600">{item.metrics}</div>
                    </LineageNode>
                  ))}
                </div>
              </motion.div>
              
              <div className="text-center mt-8">
                <PrimaryButton 
                  as={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Explore the Interactive Demo
                </PrimaryButton>
              </div>
            </LineageVisualContainer>
          </Section>
          
          {/* Column-Level Deep Dive Section */}
          <Section>
            <FeatureHighlight
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <SectionTitle>Column-Level Deep Dive</SectionTitle>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="mb-4">
                    With Qupid's column-level lineage, you can track data transformations at the most granular level. Understand exactly how each column is created, transformed, and utilized across your data stack.
                  </p>
                  <ul className="space-y-2">
                    {[
                      "Follow the exact flow of sensitive data columns",
                      "See SQL and code transformations affecting specific fields",
                      "Identify where data accuracy issues originate"
                    ].map((item, index) => (
                      <li key={`benefit-${index}`} className="flex items-start">
                        <svg className="w-5 h-5 mr-2 text-purple-300 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <pre className="text-xs text-white/90">
{`SELECT 
  c.customer_id,
  c.name,
  c.email,           -- PII column tracked
  o.order_date,
  o.total_amount,
  CASE 
    WHEN SUM(o.total_amount) > 10000 
    THEN 'VIP' 
    ELSE 'Regular'
  END AS customer_segment  -- Derived column`}
                  </pre>
                  <div className="mt-4 text-sm font-medium">
                    Column tracking shows exactly where customer_segment is used in 7 downstream assets
                  </div>
                </div>
              </div>
            </FeatureHighlight>
          </Section>
          
          {/* Future-Proof Technology Section */}
          <Section className="bg-gray-50 p-8 rounded-lg">
            <SectionTitle>Future-Proof Lineage Technology</SectionTitle>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="mb-4">
                  Qupid's lineage engine is built on a modern architecture designed for scale and extensibility. Our system automatically parses SQL queries, integrates with data tools via APIs, and builds a comprehensive knowledge graph of your data ecosystem.
                </p>
                <p>
                  The visualization layer provides both high-level and detailed views with interactive controls that let you see exactly what you need. Toggle between different views, zoom in to specific areas, use the mini-map for navigation, and take action directly from the lineage interface.
                </p>
              </div>
              <div>
                <Card>
                  <h4 className="font-bold text-lg mb-3">Integration Ecosystem</h4>
                  <div className="grid grid-cols-3 gap-3">
                    {integrationPlatforms.map((tech, index) => (
                      <div key={index} className="bg-gray-50 p-2 text-center text-sm rounded">
                        {tech}
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-center">
                    <a href="/integrations" className="text-purple-600 hover:text-purple-800 text-sm font-medium">
                      View all 30+ integrations →
                    </a>
                  </div>
                </Card>
              </div>
            </div>
            <p className="mt-6">
              With open APIs and a flexible integration framework, Qupid connects to your entire data stack today and is ready to incorporate new tools as your ecosystem evolves. Whether it's traditional data warehouses, modern cloud platforms, or specialized BI tools, Qupid provides a unified view of your complete data lineage.
            </p>
          </Section>
          
          {/* CTA Section */}
          <Section className="mt-12 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-8 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <SectionTitle className="text-white">See Qupid Lineage in Action</SectionTitle>
                <p className="mb-6">
                  Experience how Qupid's advanced lineage capabilities can transform your data governance and give you unprecedented visibility into your data ecosystem.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button 
                    as={motion.a}
                    href="/#request-demo" 
                    className="bg-white text-purple-700"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Schedule a Live Demo
                  </Button>
                  <Button 
                    as={motion.a}
                    href="/lineage-whitepaper" 
                    className="border border-white"
                    whileHover={{ scale: 1.05, backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Download Whitepaper
                  </Button>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-t from-purple-600/70 to-transparent rounded-lg z-10"></div>
                  <div className="bg-purple-200 h-64 rounded-lg"></div>
                  <div className="absolute inset-0 flex items-center justify-center z-20">
                    <motion.div 
                      className="w-16 h-16 bg-white rounded-full flex items-center justify-center cursor-pointer"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-purple-600" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </Section>
        </>
      }
    />
  );
};

export default LineagePage; 